"use client";

import { useActionState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/analytics/events";
import { ArrowRight } from "lucide-react";
import { newsletterSignupAction, type NewsletterSignupState } from "@/lib/pages/newsletter-actions";
import type { Lang } from "@/lib/i18n";
import type { ReactNode } from "react";

const initialState: NewsletterSignupState = { status: "idle" };

/**
 * The Newsletter `form` layout's interactive half. Geometry and type sizes
 * mirror the reference band exactly (px-4 py-3 rounded-lg inputs in a
 * max-w-lg row, text-sm, arrow on the button); colors are theme tokens.
 *
 * A guest's submit redirects into registration with their details stashed
 * server-side; a signed-in member's submit is a real one-click subscribe and
 * swaps the row for `successText` (see newsletterSignupAction for why).
 *
 * `inert` renders the form display-only for the editor canvas, where a submit
 * must not fire a real action.
 */
export function NewsletterSignupForm({
  lang,
  registerHref,
  namePlaceholder,
  emailPlaceholder,
  buttonLabel,
  successText,
  errorText,
  inert,
  children,
}: {
  lang: Lang;
  registerHref: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  buttonLabel: string;
  successText: string;
  errorText: string;
  inert?: boolean;
  /** The fine print row, rendered inside the form's vertical rhythm. */
  children?: ReactNode;
}) {
  const [state, formAction, pending] = useActionState(newsletterSignupAction, initialState);
  // Once per sign-up; the placement is the page the form sits on.
  const path = usePathname();
  const reported = useRef(false);
  useEffect(() => {
    if (state.status !== "subscribed" || reported.current) return;
    reported.current = true;
    trackEvent("newsletter_signup", { placement: path ?? "" });
  }, [state.status, path]);

  if (state.status === "subscribed") {
    return (
      <p className="text-primary mx-auto max-w-lg text-sm font-medium" role="status">
        {successText}
      </p>
    );
  }

  const input =
    "flex-1 px-4 py-3 rounded-lg border border-border bg-card text-sm text-foreground " +
    "placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 " +
    "focus:ring-primary/30 focus:border-primary";

  return (
    <form action={formAction} className="space-y-4" inert={inert || undefined}>
      <input type="hidden" name="lang" value={lang} />
      <input type="hidden" name="href" value={registerHref} />
      <div className="mx-auto flex max-w-lg flex-col gap-3 sm:flex-row">
        <input
          type="text"
          name="name"
          placeholder={namePlaceholder}
          required
          minLength={2}
          autoComplete="name"
          className={input}
        />
        <input
          type="email"
          name="email"
          placeholder={emailPlaceholder}
          required
          autoComplete="email"
          className={input}
        />
        <button
          type="submit"
          disabled={pending}
          className="bg-primary text-primary-foreground inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {buttonLabel}
          <ArrowRight className="size-4" aria-hidden />
        </button>
      </div>
      {state.status === "error" ? (
        <p className="text-destructive text-xs" role="alert">
          {errorText}
        </p>
      ) : null}
      {children}
    </form>
  );
}
